import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import './AffiliatePortal.css';

/* ── Currency formatting ─────────────────────────────────────────── */
const CURRENCY_SYMBOLS = { NGN: '\u20A6', USD: '$', GBP: '\u00A3', EUR: '\u20AC', GHS: 'GH\u20B5', KES: 'KSh' };

function formatCurrency(amount, currency = 'NGN') {
  const symbol = CURRENCY_SYMBOLS[currency] || currency + ' ';
  const value = Number(amount) || 0;
  // Naira values are typically stored in units (not subunits)
  if (currency === 'USD' || currency === 'GBP' || currency === 'EUR') {
    return `${symbol}${(value / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${symbol}${Math.round(value).toLocaleString()}`;
}

function formatNumber(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return v.toLocaleString();
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── Inline SVG Icons (Lucide/Feather style) ─────────────────────── */
const iconProps = { width: 20, height: 20, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', viewBox: '0 0 24 24' };

function IconMousePointer() {
  return (
    <svg {...iconProps}>
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
      <path d="M13 13l6 6" />
    </svg>
  );
}

function IconTarget() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function IconDollarSign() {
  return (
    <svg {...iconProps}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  );
}

function IconTrendingUp() {
  return (
    <svg {...iconProps}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconCheckCircle() {
  return (
    <svg {...iconProps}>
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function IconExternalLink() {
  return (
    <svg {...iconProps} width={14} height={14}>
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function IconAlertCircle() {
  return (
    <svg {...iconProps} width={48} height={48}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg {...iconProps}>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg {...iconProps}>
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg {...iconProps} width={14} height={14}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconShieldOff() {
  return (
    <svg {...iconProps} width={48} height={48}>
      <path d="M19.69 14a6.9 6.9 0 00.31 2M5 5l14 14" />
      <path d="M19.69 14a6.9 6.9 0 00.31 2" />
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ── Loading Spinner ──────────────────────────────────────────────── */
function Spinner() {
  return (
    <div className="ap-spinner-wrap">
      <div className="ap-spinner" />
      <p className="ap-spinner-text">Loading affiliate dashboard...</p>
    </div>
  );
}

/* ── Status Badge ─────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  const classMap = {
    PENDING: 'ap-badge-pending',
    APPROVED: 'ap-badge-approved',
    PAID: 'ap-badge-paid',
    CANCELLED: 'ap-badge-cancelled',
    ACTIVE: 'ap-badge-active',
    DEACTIVATED: 'ap-badge-deactivated',
  };
  return <span className={`ap-badge ${classMap[s] || 'ap-badge-default'}`}>{s}</span>;
}

/* ════════════════════════════════════════════════════════════════════
   AffiliatePortal Component
   ════════════════════════════════════════════════════════════════════ */
export default function AffiliatePortal() {
  const { referralCode } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);

  const fetchPortalData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await axiosInstance.get(`/affiliates/portal/${referralCode}`);
      if (res.data.success) {
        setData(res.data);
      } else {
        setError('Unexpected response from server.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err.response?.data?.message || 'Failed to load affiliate data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [referralCode]);

  useEffect(() => {
    if (referralCode) fetchPortalData();
  }, [referralCode, fetchPortalData]);

  /* ── Loading State ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="ap-page">
        <Spinner />
      </div>
    );
  }

  /* ── 404 State ───────────────────────────────────────────────── */
  if (notFound) {
    return (
      <div className="ap-page">
        <div className="ap-empty-state">
          <IconAlertCircle />
          <h2 className="ap-empty-title">Affiliate Not Found</h2>
          <p className="ap-empty-desc">
            The referral code <strong>{referralCode}</strong> does not match any affiliate in our system.
            Please check the URL and try again, or contact the merchant for assistance.
          </p>
        </div>
      </div>
    );
  }

  /* ── Error State ─────────────────────────────────────────────── */
  if (error) {
    return (
      <div className="ap-page">
        <div className="ap-empty-state">
          <IconAlertCircle />
          <h2 className="ap-empty-title">Something went wrong</h2>
          <p className="ap-empty-desc">{error}</p>
          <button className="ap-btn-retry" onClick={fetchPortalData}>
            <IconRefresh />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { affiliate, links = [], commissionSummary = {}, commissions = [], store = {} } = data;
  const currency = store.currency || 'NGN';

  /* ── Deactivated State ───────────────────────────────────────── */
  if (affiliate.status === 'DEACTIVATED' || affiliate.status === 'deactivated') {
    return (
      <div className="ap-page">
        {/* Header */}
        <header className="ap-header">
          <div className="ap-header-brand">
            {store.logo && <img src={store.logo} alt={store.name} className="ap-header-logo" />}
            <div>
              <h1 className="ap-header-store">{store.name || 'Store'}</h1>
              <p className="ap-header-subtitle">Affiliate Dashboard</p>
            </div>
          </div>
        </header>

        <div className="ap-empty-state">
          <IconShieldOff />
          <h2 className="ap-empty-title">Account Deactivated</h2>
          <p className="ap-empty-desc">
            Your affiliate account has been deactivated. Please contact the merchant for more information.
          </p>
        </div>
      </div>
    );
  }

  const commissionLabel = affiliate.commissionType === 'PERCENTAGE' || affiliate.commissionType === 'percentage'
    ? `${affiliate.commissionRate}%`
    : formatCurrency(affiliate.commissionRate, currency);

  /* ── Main Render ─────────────────────────────────────────────── */
  return (
    <div className="ap-page">
      {/* ─ Header ─────────────────────────────────────────────── */}
      <header className="ap-header">
        <div className="ap-header-brand">
          {store.logo && <img src={store.logo} alt={store.name} className="ap-header-logo" />}
          <div>
            <h1 className="ap-header-store">{store.name || 'Store'}</h1>
            <p className="ap-header-subtitle">Affiliate Dashboard</p>
          </div>
        </div>
      </header>

      {/* ─ Welcome ────────────────────────────────────────────── */}
      <section className="ap-welcome">
        <div className="ap-welcome-text">
          <h2 className="ap-welcome-greeting">Welcome back, {affiliate.name}!</h2>
          <div className="ap-welcome-meta">
            <span className="ap-referral-code">
              <IconLink />
              Referral Code: <strong>{affiliate.referralCode}</strong>
            </span>
            <span className="ap-commission-badge">
              <IconTrendingUp />
              {commissionLabel} commission
            </span>
          </div>
        </div>
      </section>

      {/* ─ Stats Row ──────────────────────────────────────────── */}
      <section className="ap-stats-row">
        <div className="ap-stat-card">
          <div className="ap-stat-icon ap-stat-icon--blue">
            <IconMousePointer />
          </div>
          <div className="ap-stat-body">
            <span className="ap-stat-label">Total Clicks</span>
            <span className="ap-stat-value">{formatNumber(affiliate.totalClicks)}</span>
          </div>
        </div>

        <div className="ap-stat-card">
          <div className="ap-stat-icon ap-stat-icon--purple">
            <IconTarget />
          </div>
          <div className="ap-stat-body">
            <span className="ap-stat-label">Conversions</span>
            <span className="ap-stat-value">{formatNumber(affiliate.totalConversions)}</span>
          </div>
        </div>

        <div className="ap-stat-card">
          <div className="ap-stat-icon ap-stat-icon--green">
            <IconDollarSign />
          </div>
          <div className="ap-stat-body">
            <span className="ap-stat-label">Revenue Generated</span>
            <span className="ap-stat-value">{formatCurrency(affiliate.totalRevenue, currency)}</span>
          </div>
        </div>

        <div className="ap-stat-card">
          <div className="ap-stat-icon ap-stat-icon--amber">
            <IconTrendingUp />
          </div>
          <div className="ap-stat-body">
            <span className="ap-stat-label">Commission Earned</span>
            <span className="ap-stat-value">{formatCurrency(affiliate.totalCommission, currency)}</span>
          </div>
        </div>
      </section>

      {/* ─ Commission Breakdown ───────────────────────────────── */}
      <section className="ap-section">
        <h3 className="ap-section-title">Commission Breakdown</h3>
        <div className="ap-breakdown-row">
          <div className="ap-breakdown-card ap-breakdown--pending">
            <div className="ap-breakdown-top">
              <IconClock />
              <span className="ap-breakdown-label">Pending</span>
            </div>
            <span className="ap-breakdown-amount">{formatCurrency(commissionSummary.pending?.amount, currency)}</span>
            <span className="ap-breakdown-count">{commissionSummary.pending?.count || 0} commissions</span>
          </div>

          <div className="ap-breakdown-card ap-breakdown--approved">
            <div className="ap-breakdown-top">
              <IconCheckCircle />
              <span className="ap-breakdown-label">Approved</span>
            </div>
            <span className="ap-breakdown-amount">{formatCurrency(commissionSummary.approved?.amount, currency)}</span>
            <span className="ap-breakdown-count">{commissionSummary.approved?.count || 0} commissions</span>
          </div>

          <div className="ap-breakdown-card ap-breakdown--paid">
            <div className="ap-breakdown-top">
              <IconDollarSign />
              <span className="ap-breakdown-label">Paid</span>
            </div>
            <span className="ap-breakdown-amount">{formatCurrency(commissionSummary.paid?.amount, currency)}</span>
            <span className="ap-breakdown-count">{commissionSummary.paid?.count || 0} commissions</span>
          </div>
        </div>
      </section>

      {/* ─ Links Performance Table ────────────────────────────── */}
      {links.length > 0 && (
        <section className="ap-section">
          <h3 className="ap-section-title">Links Performance</h3>
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Short URL</th>
                  <th>Clicks</th>
                  <th>Conversions</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td className="ap-td-title">{link.title || '—'}</td>
                    <td>
                      <a href={link.shortUrl} target="_blank" rel="noopener noreferrer" className="ap-link-url">
                        {link.shortUrl}
                        <IconExternalLink />
                      </a>
                    </td>
                    <td>{formatNumber(link.clicks)}</td>
                    <td>{formatNumber(link.conversions)}</td>
                    <td>{formatCurrency(link.revenue, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ─ Commission History Table ───────────────────────────── */}
      {commissions.length > 0 && (
        <section className="ap-section">
          <h3 className="ap-section-title">Commission History</h3>
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order Value</th>
                  <th>Rate</th>
                  <th>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c, i) => (
                  <tr key={i}>
                    <td className="ap-td-date">
                      <IconCalendar />
                      {formatDate(c.createdAt)}
                    </td>
                    <td>{formatCurrency(c.orderValue, currency)}</td>
                    <td>{c.commissionRate}%</td>
                    <td className="ap-td-amount">{formatCurrency(c.commissionAmount, currency)}</td>
                    <td><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ─ Footer ─────────────────────────────────────────────── */}
      <footer className="ap-footer">
        <p>Powered by <strong>Revstar</strong></p>
      </footer>
    </div>
  );
}
